import { CHAT_DIALOG_SURFACE_CLASS_NAME } from "@/features/chat/chat-dialog-styles";
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PDFViewer from "@/components/PDFViewer/PDFViewer";
import ImageViewer from "@/components/MediaViewer/ImageViewer";
import { saveBlob } from "@/ajax/documents/saveBlob";
import { DownloadWarningDialog } from "./DownloadWarningDialog";

interface ChatAttachmentPreviewModalProps {
  url: string;
  fileName: string;
  mimeType: string;
  onClose: () => void;
}

export const ChatAttachmentPreviewModal = ({
  url,
  fileName,
  mimeType,
  onClose,
}: ChatAttachmentPreviewModalProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);

  const isPDF = mimeType === "application/pdf";
  const isImage = mimeType.startsWith("image/");

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download");
      }
      const blob = await response.blob();
      await saveBlob({ blob, fileName });
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={`${CHAT_DIALOG_SURFACE_CLASS_NAME} gap-4 overflow-y-auto p-4 pt-5 sm:p-6`}
      >
        <DialogTitle className="min-w-0 pr-10 text-base font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere]">
          {fileName}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Preview and download the attached file
        </DialogDescription>

        {isPDF && (
          <PDFViewer
            url={url}
            containerClassName="h-[50dvh] min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
          />
        )}
        {isImage && (
          <ImageViewer
            url={url}
            alt={fileName}
            containerClassName="h-[50dvh] min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
          />
        )}

        <Button
          variant="outline"
          onClick={() => setShowDownloadWarning(true)}
          disabled={isDownloading}
          className="w-full shrink-0 touch-manipulation"
        >
          {isDownloading ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Download className="h-5 w-5 mr-2" />
          )}
          Download
        </Button>
      </DialogContent>
      <DownloadWarningDialog
        open={showDownloadWarning}
        onOpenChange={setShowDownloadWarning}
        onConfirm={handleDownload}
        fileName={fileName}
      />
    </Dialog>
  );
};
